import { useEffect, useRef, useState } from 'react';

import {
    sameTimeDeltaPct,
    toHourOfDaySeries,
} from '@apps/web/components/admin/outreach_observability/comparison_utils';
import {
    TV_RECS_AMBER_S,
    TV_RECS_RED_S,
    TV_WAIT_AMBER_S,
    TV_WAIT_RED_S,
} from '@apps/web/components/admin/outreach_observability/constants';
import { GhostLineChart } from '@apps/web/components/admin/outreach_observability/ghost_line_chart';
import { cn } from '@apps/web/lib/utils';
import {
    type GetOutreachTvResponse,
    type TvHourlyBucketRead,
    type TvModelCohortRead,
} from '@libraries/typescript/api_client';

// The GhostLineChart legend + gap sit above the fixed-height plot area, so the
// measured column height is reduced by that chrome to avoid overflow.
const CHART_CHROME_PX = 48;

// Shared shape of the response and recs window summaries.
type MetricSummary = {
    readonly run_count: number;
    readonly avg_wait_ms: number | null;
    readonly p50_wait_ms: number | null;
    readonly p75_wait_ms: number | null;
    readonly p95_wait_ms: number | null;
};

// Compact form for the chart axis/tooltip, where "2m 20s" wraps the gutter.
function formatWaitMinutes(ms: number): string {
    if (ms < 60_000) {
        return `${Math.round(ms / 1000)}s`;
    }
    return `${(ms / 60_000).toFixed(1)}m`;
}

function formatWaitMs(ms: number | null): string {
    if (ms === null) {
        return '—';
    }
    const totalS = Math.round(ms / 1000);
    if (totalS < 60) {
        return `${totalS}s`;
    }
    return `${Math.floor(totalS / 60)}m ${totalS % 60}s`;
}

// Green under amber, amber up to red, red past it — the three thresholds that
// make the KPI and stat chips legible from across the room.
function waitColorClass(
    ms: number | null,
    amberS: number,
    redS: number,
): string {
    if (ms === null) {
        return 'text-zinc-500';
    }
    const seconds = ms / 1000;
    if (seconds < amberS) {
        return 'text-emerald-400';
    }
    if (seconds < redS) {
        return 'text-amber-400';
    }
    return 'text-red-400';
}

// Signed percent change of today's avg wait against a comparison window. Lower
// is faster, so a drop reads green; a dash when either side has no runs.
function DeltaChip({
    label,
    todayAvgMs,
    comparisonAvgMs,
}: {
    readonly label: string;
    readonly todayAvgMs: number | null;
    readonly comparisonAvgMs: number | null;
}) {
    const pct =
        todayAvgMs === null || comparisonAvgMs === null
            ? null
            : sameTimeDeltaPct(todayAvgMs, comparisonAvgMs);
    if (pct === null) {
        return (
            <span className="inline-flex items-center gap-1.5 text-zinc-400 tabular-nums">
                <span className="uppercase">{label}</span>
                <span>—</span>
            </span>
        );
    }
    const isFaster = pct < 0;
    return (
        <span
            className={cn(
                'inline-flex items-center gap-1.5 tabular-nums',
                isFaster ? 'text-emerald-400' : 'text-red-400',
            )}
        >
            <span className="text-zinc-400 uppercase">{label}</span>
            <span className="font-semibold">
                {isFaster ? '▼' : '▲'}
                {Math.abs(pct).toFixed(0)}%
            </span>
        </span>
    );
}

function StatChip({
    label,
    valueMs,
    amberS,
    redS,
}: {
    readonly label: string;
    readonly valueMs: number | null;
    readonly amberS: number;
    readonly redS: number;
}) {
    return (
        <div className="flex flex-col gap-1 rounded-lg border border-zinc-800 bg-zinc-900/60 px-5 py-3">
            <span className="text-sm font-medium tracking-wide text-zinc-400 uppercase">
                {label}
            </span>
            <span
                className={cn(
                    'text-3xl font-semibold tabular-nums',
                    waitColorClass(valueMs, amberS, redS),
                )}
            >
                {formatWaitMs(valueMs)}
            </span>
        </div>
    );
}

// One KPI column: hourly chart on top, then the giant number, delta chips,
// percentile chips, and meta line under it.
function MetricPanel({
    title,
    today,
    yesterdaySameTimeAvgMs,
    trailing7dAvgMs,
    yesterdayFullAvgMs,
    hourlyToday,
    hourlyYesterday,
    amberS,
    redS,
    meta,
}: {
    readonly title: string;
    readonly today: MetricSummary;
    readonly yesterdaySameTimeAvgMs: number | null;
    readonly trailing7dAvgMs: number | null;
    readonly yesterdayFullAvgMs: number | null;
    readonly hourlyToday: Array<TvHourlyBucketRead>;
    readonly hourlyYesterday: Array<TvHourlyBucketRead>;
    readonly amberS: number;
    readonly redS: number;
    readonly meta: string;
}) {
    const hasRuns = today.run_count > 0;
    const todayPoints = toHourOfDaySeries(
        hourlyToday.map((bucket) => ({
            bucket_start: bucket.hour_start,
            value: bucket.avg_wait_ms,
        })),
        (bucket) => bucket.value,
    );
    const yesterdayPoints = toHourOfDaySeries(
        hourlyYesterday.map((bucket) => ({
            bucket_start: bucket.hour_start,
            value: bucket.avg_wait_ms,
        })),
        (bucket) => bucket.value,
    );

    // The shared chart takes a fixed pixel height, so the chart area is
    // measured to let the plot fill whatever the screen leaves it.
    const chartAreaRef = useRef<HTMLDivElement | null>(null);
    const [chartHeight, setChartHeight] = useState(240);
    useEffect(() => {
        const element = chartAreaRef.current;
        if (element === null) {
            return;
        }
        const observer = new ResizeObserver((entries) => {
            const height = entries[0]?.contentRect.height;
            if (height !== undefined && height > 0) {
                setChartHeight(
                    Math.max(160, Math.floor(height) - CHART_CHROME_PX),
                );
            }
        });
        observer.observe(element);
        return () => {
            observer.disconnect();
        };
    }, []);

    return (
        <div className="flex min-h-0 min-w-0 flex-col gap-4">
            <span className="text-base font-medium tracking-wide text-zinc-400 uppercase">
                {title}
            </span>
            <div ref={chartAreaRef} className="min-h-0 flex-1">
                <GhostLineChart
                    today={todayPoints}
                    ghosts={[{ label: 'Yesterday', points: yesterdayPoints }]}
                    valueFormatter={formatWaitMinutes}
                    height={chartHeight}
                />
            </div>
            <div className="flex flex-col gap-3">
                <span
                    className={cn(
                        'text-[clamp(2.5rem,5.5vw,7rem)] leading-none font-semibold tracking-tight tabular-nums',
                        hasRuns
                            ? waitColorClass(today.avg_wait_ms, amberS, redS)
                            : 'text-zinc-600',
                    )}
                >
                    {hasRuns ? formatWaitMs(today.avg_wait_ms) : '—'}
                </span>
                <div className="flex flex-wrap items-center gap-x-8 gap-y-2 text-xl">
                    <DeltaChip
                        label="vs yesterday"
                        todayAvgMs={today.avg_wait_ms}
                        comparisonAvgMs={yesterdaySameTimeAvgMs}
                    />
                    <DeltaChip
                        label="vs 7-day"
                        todayAvgMs={today.avg_wait_ms}
                        comparisonAvgMs={trailing7dAvgMs}
                    />
                </div>
            </div>
            <div className="flex flex-wrap gap-4">
                <StatChip
                    label="p50"
                    valueMs={today.p50_wait_ms}
                    amberS={amberS}
                    redS={redS}
                />
                <StatChip
                    label="p75"
                    valueMs={today.p75_wait_ms}
                    amberS={amberS}
                    redS={redS}
                />
                <StatChip
                    label="p95"
                    valueMs={today.p95_wait_ms}
                    amberS={amberS}
                    redS={redS}
                />
            </div>
            <div className="flex flex-col gap-1 text-sm text-zinc-500 tabular-nums">
                <span>{meta}</span>
                <span>
                    yesterday full day: {formatWaitMs(yesterdayFullAvgMs)}
                </span>
            </div>
        </div>
    );
}

const TV_TREATMENT_STORAGE_KEY = 'tv-model-split-treatment';

// Per-cohort model A/B strip under the main board: the frozen baseline next to
// a selectable treatment cohort (wait today + vs yesterday delta + first-recs
// today). The selection persists across wallboard reloads.
function ModelSplitStrip({
    split,
}: {
    readonly split: Array<TvModelCohortRead>;
}) {
    const [selectedTreatment, setSelectedTreatment] = useState<string | null>(
        () => localStorage.getItem(TV_TREATMENT_STORAGE_KEY),
    );
    const baseline = split[0];
    const candidates = split.slice(1);
    const treatment =
        candidates.find((cohort) => cohort.model === selectedTreatment) ??
        candidates[0];
    if (!baseline || !treatment) {
        return null;
    }
    const shown = [baseline, treatment];
    return (
        <div className="grid grid-cols-2 gap-12 border-t border-zinc-800 pt-6">
            {shown.map((cohort) => (
                <div key={cohort.model} className="flex flex-col gap-3">
                    <div className="flex items-baseline justify-between">
                        {cohort === baseline ? (
                            <span className="text-base font-medium tracking-wide text-zinc-300">
                                {cohort.model}
                            </span>
                        ) : (
                            <select
                                value={treatment.model}
                                onChange={(event) => {
                                    setSelectedTreatment(event.target.value);
                                    localStorage.setItem(
                                        TV_TREATMENT_STORAGE_KEY,
                                        event.target.value,
                                    );
                                }}
                                className="cursor-pointer rounded border border-zinc-700 bg-zinc-900 px-1.5 py-0.5 text-base font-medium tracking-wide text-zinc-300"
                            >
                                {candidates.map((candidate) => (
                                    <option
                                        key={candidate.model}
                                        value={candidate.model}
                                    >
                                        {candidate.model}
                                    </option>
                                ))}
                            </select>
                        )}
                        <span className="text-sm text-zinc-500 tabular-nums">
                            {cohort.today.run_count} runs today
                        </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-4">
                        <StatChip
                            label={`wait today · n=${cohort.today.run_count}`}
                            valueMs={cohort.today.avg_wait_ms}
                            amberS={TV_WAIT_AMBER_S}
                            redS={TV_WAIT_RED_S}
                        />
                        <StatChip
                            label={`first recs today · n=${cohort.first_recs_today.run_count}`}
                            valueMs={cohort.first_recs_today.avg_wait_ms}
                            amberS={TV_RECS_AMBER_S}
                            redS={TV_RECS_RED_S}
                        />
                        <div className="text-lg">
                            <DeltaChip
                                label="vs yesterday"
                                todayAvgMs={cohort.today.avg_wait_ms}
                                comparisonAvgMs={
                                    cohort.yesterday_full.avg_wait_ms
                                }
                            />
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
}

export function TvLatencyBoard({
    data,
}: {
    readonly data: GetOutreachTvResponse;
}) {
    const recs = data.first_recs;
    const modelSplit = data.model_split ?? null;
    return (
        <div className="flex min-h-0 flex-1 flex-col gap-8">
            <div className="grid min-h-0 flex-1 grid-cols-2 gap-12">
                <MetricPanel
                    title="Avg wait for first response · today (ET)"
                    today={data.today}
                    yesterdaySameTimeAvgMs={
                        data.yesterday_same_time.avg_wait_ms
                    }
                    trailing7dAvgMs={data.trailing_7d_same_time.avg_wait_ms}
                    yesterdayFullAvgMs={data.yesterday_full.avg_wait_ms}
                    hourlyToday={data.hourly_today}
                    hourlyYesterday={data.hourly_yesterday}
                    amberS={TV_WAIT_AMBER_S}
                    redS={TV_WAIT_RED_S}
                    meta={`queue ${formatWaitMs(data.today.avg_queue_ms)} · generation ${formatWaitMs(data.today.avg_generation_ms)} · ${data.today.run_count} runs today`}
                />
                <MetricPanel
                    title="Avg time to first recommendations · today (ET)"
                    today={recs.today}
                    yesterdaySameTimeAvgMs={
                        recs.yesterday_same_time.avg_wait_ms
                    }
                    trailing7dAvgMs={recs.trailing_7d_same_time.avg_wait_ms}
                    yesterdayFullAvgMs={recs.yesterday_full.avg_wait_ms}
                    hourlyToday={recs.hourly_today}
                    hourlyYesterday={recs.hourly_yesterday}
                    amberS={TV_RECS_AMBER_S}
                    redS={TV_RECS_RED_S}
                    meta={`${recs.today.run_count} runs with recommendations today`}
                />
            </div>
            {modelSplit && modelSplit.length > 0 ? (
                <ModelSplitStrip split={modelSplit} />
            ) : null}
        </div>
    );
}
